import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ExceptionManager } from "Core/logManager";
import { clientQuery, clientMutation } from "Core/data/GraphQLApi";
import { endpoints } from "Core/defaultValues";
import { upsertManyWithRules as upsertRealtimeDrivers } from "Core/realtime/redux/driverSlice";
import { buildDriverRowFromSocket, doesDriverMatchView } from "../../../utils";

const INIT_STATE = {

  //createDriver
  driverResponse: null,
  loading: false,
  error: null,

  //searchDrivers
  entity: { id: "drivers" },
  loading: false,
  total: 0,
  filters: {
    tagIds: [], 
    unitIds: [], 
    units: [], 
    tags: [],
    entity: 'All', 
    includeInactiveDrivers: true, 
    unassigned: true 
  },
  registers: [],
  driverInfo:{},
  errorSearch: null,
  openFilters: false,
  searchText: null,
  pendingSocketDrivers: [],
  missingSupervisor: false,

  //openCreateEdit
  openCreateEdit: false,
  driverGroup: null,
  supervisor: [],
  loadingSupervisor: false,
  errorSupervisor: null,
};

const route = "modules/settings/views/driver/redux/slice";

const getRegisterId = (driver) => String(driver?.driverId || driver?.id);

// Applies socket driver events (create/edit/delete) over the loaded rows in one pass
const applySocketDrivers = (state, socketDrivers) => {
  const registers = state.registers || [];
  const indexById = new Map(registers.map((driver, index) => [getRegisterId(driver), index]));
  const removedIds = new Set();
  const viewParams = { filters: state.filters, searchText: state.searchText, supervisors: state.supervisor };

  socketDrivers.forEach((item) => {
    const driverId = String(item.id || item.driverId || item.entityId);
    const index = indexById.get(driverId);
    const isDeleted = item.event === "deleted" || Boolean(item._deleted);
    const row = isDeleted ? null : buildDriverRowFromSocket(item, index !== undefined ? registers[index] : null);

    if (row && item.supervisorId !== undefined) {
      const supervisor = state.supervisor?.find((user) => String(user?.id) === String(item.supervisorId));
      row.supervisor = supervisor ? { firstName: supervisor.firstName, lastName: supervisor.lastName } : null;
      if (!supervisor && Number(item.supervisorId) > 0) state.missingSupervisor = true;
    }

    if (!row || !doesDriverMatchView(row, viewParams)) {
      if (index !== undefined) removedIds.add(driverId);
      return;
    }

    removedIds.delete(driverId);
    if (index !== undefined) {
      registers[index] = row;
      return;
    }
    indexById.set(driverId, registers.length);
    registers.push(row);
  });

  state.registers = removedIds.size
    ? registers.filter((driver) => !removedIds.has(getRegisterId(driver)))
    : registers;
};

export const searchDrivers = createAsyncThunk(
  "settings/driver/searchDrivers",
  async (payload, { rejectWithValue, dispatch }) => {
    const { conditions, sort, limit = 10, offset = 0 } = payload;

    try {
      let data = await clientQuery(
        `
        query searchDrivers ($conditions: [DriverSearchInput], $sort: [DriverSortInput], $limit: Int, $offset: Int) {
        searchDrivers (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
            items {
                driverId
                externalId
                name
                idTimeZone
                tagIds
                status
                licenseNumber
                licenseCountry
                licenseState
                driverPin
                hosRulesetIds
                hosExempt
                hosExemptComment
                email
                phone
                empNum
                licenseExpDate
                notifyByEmail
                notifyBySms
                supervisorId
                supervisor {
                    firstName
                    lastName
                }
                fsmDriver {
                    username
                }
                keyfob {
                    hex
                }
                avatarFileId
                tempAvatarUrl
                specialDutyStatuses
                avatarFile {
                    id
                    repositoryId
                    repositoryType
                    repositoryCode
                    repositoryPath
                    fileKey
                    originalName
                }
                unit {
                   id
                   label
                }
            }
            total
        }
    }
    `,
        {
          conditions,
          sort,
          limit,
          offset,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        const {
          searchDrivers: { items, total },
        } = data;
        return { items, total };
      } else {
        throw "Error while getting drivers";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "searchDrivers");
      return rejectWithValue(exc);
    }
  },
  {
    condition: (payload, { getState }) => {
      const { driverTableSliceRedux } = getState();
      return !driverTableSliceRedux.loading;
    },
  }
);

export const searchSupervisor = createAsyncThunk(
  "settings/driver/searchSupervisor",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      let data = await clientQuery(
        `
          query searchSubUsers ($conditions: [SubUserSearchInput], $sort: [SubUserSortInput], $limit: Int, $offset: Int) {
            response: searchSubUsers (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
              items {
                id
                firstName
                lastName
                userName
                isSupervisor
              }
              total
            }
          }
        `,
        {},
        endpoints.GRAPHQL_GENERAL_V2
      );

      if (data) {
        const filter = data?.response?.items?.filter(
          (item) => item?.isSupervisor === true
        );
        const createFullname = filter?.map((subUser) => {
          return {
            ...subUser,
            fullname: `${subUser?.firstName} ${subUser?.lastName}`,
          };
        });

        return createFullname;
      } else {
        throw "Error while getting supervisor users";
      }
    } catch (exc) {
      ExceptionManager(exc, route, "searchSupervisor");
      return rejectWithValue(exc);
    }
  },
  {
    condition: (payload, { getState }) => {
      const { driverTableSliceRedux } = getState();
      return !driverTableSliceRedux.loadingSupervisor;
    },
  }
);

export const driverTableSliceRedux = createSlice({
  name: "driver/driverTable",
  initialState: INIT_STATE,
  reducers: {
    toggleDriversFilters: (state, action) => {
      state.openFilters = action.payload;
    },
    manageFiltersDrivers: (state, action) => {
      state.filters = action.payload;
    },
    upsertDriverRegister: (state, action) => {
      const nextDriver = action.payload;
      if (!nextDriver) return;

      const nextDriverId = String(nextDriver?.driverId || nextDriver?.id);
      if (!nextDriverId) return;

      const existingIndex = state.registers.findIndex(
        (driver) => String(driver?.driverId || driver?.id) === nextDriverId
      );

      if (existingIndex >= 0) {
        state.registers[existingIndex] = {
          ...state.registers[existingIndex],
          ...nextDriver,
        };
        return;
      }

      state.registers.push(nextDriver);
    },
    removeDriverRegister: (state, action) => {
      const driverId = String(action.payload);
      state.registers = state.registers.filter(
        (driver) => String(driver?.driverId || driver?.id) !== driverId
      );
    },
    setDriversSearchText: (state, action) => {
      state.searchText = action.payload;
    }
  },
  extraReducers: (builder) => {
    //realtime drivers: only socket messages carry an event, catalog loads don't
    builder.addCase(upsertRealtimeDrivers, (state, { payload }) => {
      const socketDrivers = (payload || []).filter((item) => item?.event);
      if (!socketDrivers.length) return;

      // a search in flight may return data older than these events, apply them after it
      if (state.loading) {
        state.pendingSocketDrivers.push(...socketDrivers);
        return;
      }
      applySocketDrivers(state, socketDrivers);
    });

    //searchDrivers
    builder.addCase(searchDrivers.pending, (state, action) => {
      state.loading = true;
      // the search already returns current data, keep only events received after it starts
      state.pendingSocketDrivers = [];
    });
    builder.addCase(searchDrivers.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.errorSearch = null;
      state.registers = payload?.items;
      state.total = payload?.total;
      if (state.pendingSocketDrivers.length) {
        applySocketDrivers(state, state.pendingSocketDrivers);
        state.pendingSocketDrivers = [];
      }
    });
    builder.addCase(searchDrivers.rejected, (state, { payload }) => {
      state.loading = false;
      if (state.pendingSocketDrivers.length) {
        applySocketDrivers(state, state.pendingSocketDrivers);
        state.pendingSocketDrivers = [];
      }
      state.errorSearch = payload || true;
    });

    //searchSupervisor
    builder.addCase(searchSupervisor.pending, (state, action) => {
      state.loadingSupervisor = true;
    });
    builder.addCase(searchSupervisor.fulfilled, (state, { payload }) => {
      state.loadingSupervisor = false;
      state.missingSupervisor = false;
      state.supervisor = payload;
    });
    builder.addCase(searchSupervisor.rejected, (state, { payload }) => {
      state.loadingSupervisor = false;
      state.missingSupervisor = false;
      state.errorSupervisor = payload;
    });
  },
});

const { actions, reducer } = driverTableSliceRedux;

export const { openDriverCreateEditModal, manageFiltersDrivers, toggleDriversFilters, upsertDriverRegister, removeDriverRegister, setDriversSearchText } = actions;

export default reducer;
